import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import isNil from "lodash/isNil";
import cloneDeep from "lodash/cloneDeep";
import reverse from "lodash/reverse";
import size from "lodash/size";
import find from "lodash/find";
import moment from "moment";

import { useGoogleDirectionsQuery } from "globals/graphql/hooks/useGoogleDirectionsQuery";
import { useAnalytics, useOperatorSlug, useQueryParams } from "globals/hooks";
import { getInitialStop } from "../context/initialState";
import { CreateRequestReduxActionTypes } from "../context/reducer/types";
import { useCreateRequestContext } from "../context/useCreateRequestContext";
import { TripCategory } from "types";
import { allOrderTypes } from "../steps/info/components/data";
import useLoadVehicleBRA from "../steps/info/hooks/useLoadVehicleBRA";

type BookingWidgetStop = {
  description: string;
  pickUpGooglePlaceTypes?: string[];
};

type BookingWidgetTrip = {
  note: string;
  totalGroupSize: number;
  stops: BookingWidgetStop[];
  dateTime: string;
  tripCategory: TripCategory;
  orderType: string;
  totalDuration?: number;
  returnDateTime?: string;
  returnStops?: BookingWidgetStop[];
};

function useBookingWidgetTrip() {
  // form hooks
  const [
    request,
    dispatch,
    { skipVehicleDefaultVehicle, skipVehicleSelectionEnabled },
  ] = useCreateRequestContext();
  const history = useHistory();
  const operatorSlug = useOperatorSlug();
  const queryParams = useQueryParams();
  const { track } = useAnalytics();
  const [queryVehicleBRA] = useLoadVehicleBRA();
  const skipVehicleSelectionRequestRef = useRef(false);

  const [trip, setTrip] = useState<BookingWidgetTrip | null>(() =>
    queryParams.get("trip") === "success"
      ? null
      : JSON.parse(queryParams.get("trip"))
  );

  const stops = (trip?.stops || []).map((stop, index) => ({
    ...getInitialStop(index + 1),
    stopIndex: index + 1,
    dateTime:
      index === 0
        ? trip.dateTime
        : trip.totalDuration && index === size(trip?.stops) - 1 // if hourly trip, add totalDuration to get dateTime on last stop
        ? moment
            .utc(trip.dateTime)
            .add(Number(trip.totalDuration), "minutes")
            .toISOString()
        : undefined,
    location: stop.description,
    pickUpGooglePlaceTypes: stop?.pickUpGooglePlaceTypes,
  }));

  const isRoundTrip = trip?.tripCategory === TripCategory.RoundTrip;
  const explicitReturnStops = trip?.returnStops || [];
  const hasReturnStops = isRoundTrip && explicitReturnStops.length > 0;

  const [estimatedDuration, googleDirectionsResult] = useGoogleDirectionsQuery({
    stops,
    skip: !trip,
  });

  const explicitMappedReturnStops = hasReturnStops
    ? explicitReturnStops.map((stop, index) => ({
        ...getInitialStop(index + 1),
        stopIndex: index + 1,
        dateTime: index === 0 ? trip.returnDateTime : undefined,
        location: stop.description,
        pickUpGooglePlaceTypes: stop?.pickUpGooglePlaceTypes,
      }))
    : null;

  const reversedReturnStops = isRoundTrip && trip?.returnDateTime
    ? reverse(cloneDeep(stops)).map((stop, index) => ({
        ...stop,
        stopIndex: index + 1,
        dateTime: index === 0 ? trip.returnDateTime : undefined,
      }))
    : null;

  const returnStops = explicitMappedReturnStops || reversedReturnStops;

  const hasReturnTrip = hasReturnStops || !!reversedReturnStops;

  const [returnEstimatedDuration, returnGoogleDirectionsResult] =
    useGoogleDirectionsQuery({
      stops: returnStops,
      skip: !returnStops,
    });

  // use effects
  // update estimtated duration from booking widget trip
  useEffect(() => {
    if (isNil(googleDirectionsResult)) return;

    dispatch({
      type: CreateRequestReduxActionTypes.UpdateEstimatedDuration,
      payload: {
        trip: { estimatedDuration, googleDirectionsResult },
        ...(hasReturnTrip && {
          returnTrip: {
            estimatedDuration: returnEstimatedDuration,
            googleDirectionsResult: returnGoogleDirectionsResult,
          },
        }),
      },
    });
  }, [
    dispatch,
    estimatedDuration,
    googleDirectionsResult,
    returnEstimatedDuration,
    returnGoogleDirectionsResult,
    hasReturnTrip,
    trip,
  ]);

  // event handler
  const handleReceiveTripMessage = useCallback(async () => {
    if (!trip) return;

    const tripPayload = {
      stops,
      totalGroupSize: trip.totalGroupSize,
      note: trip.note || "",
      tripCategory: trip.tripCategory,
      // add additional trip params for hourly trip
      ...(trip.totalDuration && {
        totalDuration: trip.totalDuration,
        useTotalDuration: true,
      }),
    };
    const returnTripPayload = hasReturnTrip
      ? {
          stops: returnStops,
          totalGroupSize: trip.totalGroupSize,
          tripCategory: trip.tripCategory,
        }
      : undefined;

    dispatch({
      type: CreateRequestReduxActionTypes.ReceiveBookingWidgetData,
      payload: {
        // encodedQuery for orderType is using slug instead of object being used
        orderType: find(allOrderTypes, { slug: trip.orderType }),
        trip: tripPayload,
        ...(returnTripPayload && { returnTrip: returnTripPayload }),
      },
    });

    let vehicle = null;

    if (skipVehicleSelectionEnabled && skipVehicleDefaultVehicle?.id) {
      try {
        vehicle = await queryVehicleBRA({
          vehicleId: skipVehicleDefaultVehicle.id,
          trip: {
            ...request.trip,
            ...tripPayload,
          },
          returnTrip: returnTripPayload
            ? {
                ...request.returnTrip,
                ...returnTripPayload,
              }
            : null,
          tripCategory: trip.tripCategory,
        });

        if (vehicle) {
          dispatch({
            type: CreateRequestReduxActionTypes.UpdateVehiclePage,
            payload: vehicle,
          });
        }
      } catch {
        vehicle = null;
      }
    }

    const parsedQueryParams = new URLSearchParams(history.location.search);
    parsedQueryParams.set("trip", "success");
    setTrip(null);

    history.replace({
      ...history.location,
      pathname: vehicle
        ? `/${operatorSlug}/new/confirm`
        : `/${operatorSlug}/new/vehicle`,
      search: parsedQueryParams.toString(),
    });

    if (vehicle) {
      track("bookingTool_chooseVehicle", {
        type: vehicle.baseRateAutomation
          ? "BRA"
          : vehicle.settings?.pricelessBookingEnabled
          ? "Reservation Request"
          : "Quote Request",
      });
    }
  }, [
    dispatch,
    stops,
    returnStops,
    trip,
    hasReturnTrip,
    history,
    operatorSlug,
    queryVehicleBRA,
    request,
    skipVehicleDefaultVehicle,
    skipVehicleSelectionEnabled,
    track,
  ]);

  // check if 'trip' queryParams exist in order to determine which operator to load
  useEffect(() => {
    if (
      trip &&
      queryParams.get("trip") !== "success" &&
      !skipVehicleSelectionRequestRef.current
    ) {
      skipVehicleSelectionRequestRef.current = true;
      handleReceiveTripMessage();
    }
  }, [trip, handleReceiveTripMessage, queryParams]);

  return {
    isBookingWidgetTrip: !!trip || queryParams.get("trip") === "success",
  };
}

export { useBookingWidgetTrip };
